import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { QueryClient } from '@tanstack/react-query';
import {
  AUTH_EXPIRED_FLASH_MESSAGE_KEY,
  AUTH_SESSION_QUERY_KEY,
  AUTH_TOKEN_PROACTIVE_REFRESH_BUFFER_MS,
} from '../features/auth/constants';
import { mergeSessionAfterRefresh } from '../features/auth/lib/mergeSessionAfterRefresh';
import {
  clearStoredSession,
  readStoredSession,
  writeStoredSession,
} from '../features/auth/lib/authSessionStorage';
import { isLocalDevMockSession } from '../features/auth/lib/localDevAuthBypass';
import type {
  ApiResponse,
  RefreshTokenData,
} from '../features/auth/types/auth';

const baseURL = process.env.NEXT_PUBLIC_API_URL ?? '/';

export const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Same origin/headers as `api` but no auth interceptors — used only for refresh-token calls
 * so refresh failures never recurse through 401 refresh logic.
 */
export const authRefreshApi = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

type MutableAxiosRequestConfig = InternalAxiosRequestConfig & {
  __fflUsedSessionAccessToken?: boolean;
  /** Set before retrying a failed request after refresh; skips proactive refresh. */
  __fflAuthRetry401?: boolean;
};

let queryClientRef: QueryClient | null = null;

export function bindApiAuthQueryClient(client: QueryClient): void {
  queryClientRef = client;
}

let refreshInFlight: Promise<boolean> | null = null;

function flashSessionExpiredMessage(message: string): void {
  try {
    sessionStorage.setItem(AUTH_EXPIRED_FLASH_MESSAGE_KEY, message);
  } catch {
    // ignore
  }
}

function forceLogoutAfterRefreshFailure(): void {
  clearStoredSession();
  queryClientRef?.setQueryData(AUTH_SESSION_QUERY_KEY, null);
  flashSessionExpiredMessage('Your session has expired—please sign in again.');
}

function shouldProactivelyRefreshSession(): boolean {
  const session = readStoredSession();
  if (isLocalDevMockSession(session)) return false;
  const at = session?.data?.accessTokenExpiresAt;
  if (typeof at !== 'number') return false;
  return at - Date.now() < AUTH_TOKEN_PROACTIVE_REFRESH_BUFFER_MS;
}

async function executeRefresh(): Promise<boolean> {
  const session = readStoredSession();
  if (isLocalDevMockSession(session)) {
    return false;
  }
  const refreshTokenValue = session?.data?.refreshToken;
  if (!session || !refreshTokenValue) {
    forceLogoutAfterRefreshFailure();
    return false;
  }

  try {
    const { data } = await authRefreshApi.post<ApiResponse<RefreshTokenData>>(
      '/auth/refresh-token',
      { refreshToken: refreshTokenValue },
    );

    if (!data?.success || !data.data?.accessToken || !data.data?.idToken) {
      forceLogoutAfterRefreshFailure();
      return false;
    }

    const merged = mergeSessionAfterRefresh(session, data);
    if (!merged) {
      forceLogoutAfterRefreshFailure();
      return false;
    }

    writeStoredSession(merged);
    queryClientRef?.setQueryData(AUTH_SESSION_QUERY_KEY, merged);
    return true;
  } catch (err) {
    const status = (err as AxiosError).response?.status;
    if (status === 401 || status === 403) {
      forceLogoutAfterRefreshFailure();
      return false;
    }
    if (status === 400) {
      forceLogoutAfterRefreshFailure();
      return false;
    }
    forceLogoutAfterRefreshFailure();
    return false;
  }
}

function performTokenRefreshSingleFlight(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = executeRefresh();
  void refreshInFlight.finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

api.interceptors.request.use(async (config) => {
  const mutable = config as MutableAxiosRequestConfig;

  if (!mutable.__fflAuthRetry401 && shouldProactivelyRefreshSession()) {
    await performTokenRefreshSingleFlight();
  }

  const session = readStoredSession();
  const token = session?.data?.accessToken;
  if (token) {
    mutable.headers.Authorization = `Bearer ${token}`;
    mutable.__fflUsedSessionAccessToken = true;
  }

  return mutable;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as MutableAxiosRequestConfig | undefined;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !original ||
      original.__fflAuthRetry401 ||
      !original.__fflUsedSessionAccessToken
    ) {
      return Promise.reject(error);
    }

    if (isLocalDevMockSession(readStoredSession())) {
      return Promise.reject(error);
    }

    const refreshed = await performTokenRefreshSingleFlight();
    if (!refreshed) {
      return Promise.reject(error);
    }

    const session = readStoredSession();
    const newToken = session?.data?.accessToken;
    if (!newToken) {
      return Promise.reject(error);
    }

    original.__fflAuthRetry401 = true;
    original.headers = original.headers ?? {};
    original.headers.Authorization = `Bearer ${newToken}`;
    return api.request(original);
  },
);
